import { BLOCKS, MARKS } from '@contentful/rich-text-types'
import {
  AssetOptionsContext,
  type AssetOptionsContextProps,
  DEFAULT_MAX_SVG_HEIGHT,
  DEFAULT_MIN_SVG_WIDTH,
} from '@contexts/asset-options'
import { FontSizeContext } from '@contexts/fontsize'
import { GridOptionsContext } from '@contexts/grid-options'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import { withDisplayName } from '@hocs/with-display-name'
import { useMatchMedia } from '@hooks/use-match-media'
import { boldImageText } from '@render-mark/bold-image-text'
import { codeImageText } from '@render-mark/code-image-text'
import { italicBigImageText } from '@render-mark/italic-big-image-text'
import { documentCenteredPadding } from '@render-node/document-centered-padding'
import { heading3Green } from '@render-node/heading-3-green'
import { heading4ImageText } from '@render-node/heading-4-image-text'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { contentArticleTitleOptions } from '@rich-text/contentful-content-article-rich-text'
import { Hyperlink } from '@ui/hyperlink'
import Media from '@ui/media/media'
import { device } from '@utils/breakpoints'
import clsx from 'clsx'
import { graphql } from 'gatsby'
import React, { useContext } from 'react'

const assetOptions: AssetOptionsContextProps = {
  width: '100%',
  height: 'auto',
  minSvgWidth: DEFAULT_MIN_SVG_WIDTH,
  maxSvgHeight: DEFAULT_MAX_SVG_HEIGHT,
  objectFit: 'contain',
  alt: '',
}

const ContentfulContentArticleDefault: Utils.EntryFC<
  Queries.ContentfulContentArticleDefaultFragment
> = ({
  content: {
    contentArticleTitle: title,
    subtitle,
    image,
    imageText,
    content,
    contentLinkText,
    contentLinkUrl,
    videoLink,
  },
  fieldInspectorProps,
  entryInspectorProps,
}) => {
  const hasHeader = title || subtitle
  const hasFooter = contentLinkText && contentLinkUrl
  const hasImage = image || imageText || videoLink
  const hasImageLink = hasImage && !contentLinkText && contentLinkUrl
  const gridOptions = useContext(GridOptionsContext)
  const isLaptopLUp = useMatchMedia(device.laptopL)
  const forceRowLayout = gridOptions.col.laptop === 1

  return (
    <div className="w-full" {...entryInspectorProps()}>
      <div
        className={clsx(
          'mt-0 flex items-stretch group-odd/section:laptop:flex-row-reverse',
          forceRowLayout
            ? 'flex-col laptop:flex-row laptop:justify-stretch'
            : 'flex-col',
          hasImage ? 'gap-10' : 'gap-0'
        )}
      >
        {hasImage && (
          <div
            className={clsx(
              'flex w-full flex-1',
              (!hasHeader && !content && !hasFooter) || !forceRowLayout
                ? 'basis-auto'
                : 'basis-auto laptop:basis-5/12',
              imageText ? 'self-center' : 'self-center laptop:self-start'
            )}
          >
            <div className="flex w-full justify-center pb-6 laptop:pb-0">
              {image && (
                <AssetOptionsContext.Provider value={assetOptions}>
                  {hasImageLink ? (
                    <Hyperlink href={contentLinkUrl}>
                      <Entry
                        content={image}
                        mapping={{
                          [ContentType.ContentfulAsset]: ContentfulAsset,
                        }}
                      />
                    </Hyperlink>
                  ) : (
                    <Entry
                      content={image}
                      mapping={{
                        [ContentType.ContentfulAsset]: ContentfulAsset,
                      }}
                    />
                  )}
                </AssetOptionsContext.Provider>
              )}
              {imageText && (
                <FontSizeContext.Provider
                  value={!forceRowLayout && isLaptopLUp ? 6 : 16}
                >
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'imageText',
                    })}
                    content={imageText}
                    options={{
                      ...defaultRichTextFieldOptionsDeprecated,
                      renderMark: {
                        ...defaultRichTextFieldOptionsDeprecated.renderMark,
                        [MARKS.BOLD]: boldImageText,
                        [MARKS.ITALIC]: italicBigImageText,
                        [MARKS.CODE]: codeImageText,
                      },
                      renderNode: {
                        ...defaultRichTextFieldOptionsDeprecated.renderNode,
                        [BLOCKS.DOCUMENT]: documentCenteredPadding,
                        [BLOCKS.HEADING_3]: heading3Green,
                        [BLOCKS.HEADING_4]: heading4ImageText,
                      },
                    }}
                  />
                </FontSizeContext.Provider>
              )}
              {videoLink && <Media url={videoLink} />}
            </div>
          </div>
        )}
        {(hasHeader || content || hasFooter) && (
          <div
            className={clsx(
              'flex flex-1 flex-col',
              !hasImage || !forceRowLayout
                ? 'basis-auto'
                : 'basis-auto laptop:basis-7/12'
            )}
          >
            <>
              {title && (
                <RichText
                  {...fieldInspectorProps({
                    fieldId: 'title',
                  })}
                  content={title}
                  options={contentArticleTitleOptions}
                />
              )}
              {subtitle && (
                <RichText
                  {...fieldInspectorProps({
                    fieldId: 'subtitle',
                  })}
                  content={subtitle}
                  options={defaultRichTextFieldOptionsDeprecated}
                />
              )}
              {content && (
                <RichText
                  {...fieldInspectorProps({
                    fieldId: 'content',
                  })}
                  content={content}
                  options={defaultRichTextFieldOptionsDeprecated}
                />
              )}
              {hasFooter && (
                <div>
                  <Hyperlink
                    {...fieldInspectorProps({
                      fieldId: 'contentLinkText',
                    })}
                    href={contentLinkUrl}
                  >
                    {contentLinkText}
                  </Hyperlink>
                </div>
              )}
            </>
          </div>
        )}
      </div>
    </div>
  )
}

export const query = graphql`
  fragment ContentfulContentArticleDefault on Contentful4TmD9EihHakmKMkEwKaUwQ {
    ...ContentfulEntry
    contentArticleTitle: title {
      ...ContentfulContentArticleTitle
    }
    subtitle {
      ...ContentfulContentArticleSubtitle
    }
    content {
      ...ContentfulContentArticleContent
    }
    image {
      ...ContentfulDefaultAsset
    }
    imageText {
      ...ContentfulContentArticleImageText
    }
    videoLink
    contentLinkText
    contentLinkUrl
  }
`

export default withDisplayName(
  ContentfulContentArticleDefault,
  'ContentfulContentArticleDefault'
)
